"use client"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { Check, ChevronsUpDown, HelpCircle } from "lucide-react"
import { useMemo, useState } from "react"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Dropdown2Option {
  value: string
  label: string
  /** Shown in the ? tooltip. Falls back to the group's description. */
  description?: string
  /** Optional badge rendered between the label and the ? tooltip icon. */
  badge?: React.ReactNode
}

export interface Dropdown2Group {
  key: string
  label: string
  options: Dropdown2Option[]
  /** Fallback description for every option in this group */
  description?: string
}

export interface Dropdown2Props {
  value: string | null
  onChange: (value: string | null) => void
  /** Grouped options. Use `groups` or `options` — not both. */
  groups?: Dropdown2Group[]
  /** Flat (ungrouped) options. Use `groups` or `options` — not both. */
  options?: Dropdown2Option[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  /** Label shown in the trigger when nothing is selected */
  noSelectionLabel?: string
  /** Whether to show a "no selection" option at the top of the list */
  showNoSelection?: boolean
  /** Extra choices listed right under the "no selection" option and styled
   *  like it, for actions that are not one of the grouped options. */
  leadingOptions?: Dropdown2Option[]
  disabled?: boolean
  className?: string
  triggerClassName?: string
  /** Width of the trigger button (default: 220px) */
  triggerWidth?: string
  /** Width of the dropdown panel (default: 280px) */
  popoverWidth?: string
  /** Extra heading className — useful for overriding group-heading styles */
  groupHeadingClassName?: string
}

// ─── Component ────────────────────────────────────────────────────────────────

const NO_LEADING_OPTIONS: Dropdown2Option[] = []

export function Dropdown2({
  value,
  onChange,
  groups,
  options,
  placeholder,
  searchPlaceholder = "Search…",
  emptyText = "No option found.",
  noSelectionLabel = "No assignment",
  showNoSelection = true,
  leadingOptions = NO_LEADING_OPTIONS,
  disabled = false,
  className,
  triggerClassName,
  triggerWidth = "220px",
  popoverWidth = "280px",
  groupHeadingClassName,
}: Dropdown2Props) {
  const [open, setOpen] = useState(false)

  // Normalise flat options into a single anonymous group so the render path
  // below only has to handle grouped data.
  const normalisedGroups = useMemo<Dropdown2Group[]>(() => {
    if (groups) return groups
    if (options) return [{ key: "__flat__", label: "", options }]
    return []
  }, [groups, options])

  // Resolve display label for the currently selected value.
  const selectedLabel = useMemo(() => {
    if (!value) return null
    const leading = leadingOptions.find((o) => o.value === value)
    if (leading) return leading.label
    for (const group of normalisedGroups) {
      const match = group.options.find((o) => o.value === value)
      if (match) return match.label
    }
    return null
  }, [value, leadingOptions, normalisedGroups])

  const isSingleFlatGroup =
    normalisedGroups.length === 1 && normalisedGroups[0].key === "__flat__"

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn("inline-flex", className)}>
        <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
              style={{ width: triggerWidth }}
              className={cn(
                "flex h-9 items-center justify-between rounded-md border bg-background px-3 py-2 text-sm transition-colors",
                "hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50",
                !value && "text-muted-foreground",
                triggerClassName,
              )}
            >
              <span className="truncate text-left">
                {selectedLabel ?? placeholder ?? noSelectionLabel}
              </span>
              <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>

          <PopoverContent
            style={{ width: popoverWidth }}
            className="p-0"
            align="start"
            onClick={(e) => e.stopPropagation()}
          >
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList>
                <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
                  {emptyText}
                </CommandEmpty>

                {(showNoSelection || leadingOptions.length > 0) && (
                  <>
                    {showNoSelection && (
                      <CommandItem
                        value="__no_selection__"
                        onSelect={() => {
                          onChange(null)
                          setOpen(false)
                        }}
                        className="text-muted-foreground"
                      >
                        {!value && <Check className="h-3.5 w-3.5 shrink-0" />}
                        {noSelectionLabel}
                      </CommandItem>
                    )}
                    {leadingOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={() => {
                          onChange(option.value)
                          setOpen(false)
                        }}
                        className="text-muted-foreground"
                      >
                        {value === option.value && (
                          <Check className="h-3.5 w-3.5 shrink-0" />
                        )}
                        {option.label}
                      </CommandItem>
                    ))}
                    <CommandSeparator />
                  </>
                )}

                {normalisedGroups.map((group, groupIndex) => {
                  const content = (
                    <CommandGroup
                      key={group.key}
                      heading={isSingleFlatGroup ? undefined : group.label}
                      className={cn(
                        groupHeadingClassName ??
                          "[&_[cmdk-group-heading]]:font-mono **:[[cmdk-group-heading]]:text-[10px] **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:tracking-widest **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:uppercase",
                      )}
                    >
                      {group.options.map((option) => {
                        const isSelected = value === option.value
                        const helpText = option.description ?? group.description
                        return (
                          <CommandItem
                            key={option.value}
                            value={`${group.label} ${option.label}`}
                            onSelect={() => {
                              onChange(option.value)
                              setOpen(false)
                            }}
                            className="group"
                          >
                            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                              {isSelected && (
                                <Check className="h-3.5 w-3.5 text-primary" />
                              )}
                            </span>
                            <span className="flex-1 truncate">
                              {option.label}
                            </span>
                            {option.badge && (
                              <span className="ml-1 shrink-0">
                                {option.badge}
                              </span>
                            )}
                            {helpText && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    role="button"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className="ml-1 flex h-4 w-4 shrink-0 items-center justify-center rounded text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100 group-data-[selected=true]:opacity-100 hover:text-muted-foreground!"
                                  >
                                    <HelpCircle className="h-3 w-3" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  className="space-y-0.5 max-w-[200px]"
                                  onPointerDownOutside={(e) =>
                                    e.preventDefault()
                                  }
                                >
                                  {!isSingleFlatGroup && (
                                    <p className="font-semibold text-foreground">
                                      {group.label}
                                    </p>
                                  )}
                                  <p className="text-muted-foreground">
                                    {helpText}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  )

                  if (!isSingleFlatGroup && groupIndex > 0) {
                    return (
                      <>
                        <CommandSeparator key={`sep-${group.key}`} />
                        {content}
                      </>
                    )
                  }
                  return content
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  )
}
